import React, { useEffect, useRef } from 'react';
import SEO from '../components/SEO';

const servicesData = [
    {
        id: 1,
        title: 'SIGNAGE DESIGN & FABRICATION',
        icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M9 9h6M9 15h6" />
            </svg>
        ),
        description: 'LED, Neon, 3D lettering, Acrylic, Metal, Wooden, and Digital signage crafted with precision and creativity',
        features: [
            'LED & Neon Signage',
            '3D Lettering & Channel Letters',
            'Acrylic & Metal Fabrication',
            'Wooden & Digital Signage',
            'Custom Design Solutions'
        ]
    },
    {
        id: 2,
        title: 'BRANDING SOLUTIONS',
        icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
        ),
        description: 'Indoor and outdoor branding, wall graphics, vinyl wrapping, vehicle branding, and wayfinding systems',
        features: [
            'Indoor & Outdoor Branding',
            'Wall Graphics & Murals',
            'Vinyl Wrapping',
            'Vehicle Branding',
            'Wayfinding Systems'
        ]
    },
    {
        id: 3,
        title: 'INSTALLATION SERVICES',
        icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
            </svg>
        ),
        description: 'Safe and professional installation for all types of signage with meticulous attention to detail',
        features: [
            'Professional Installation Team',
            'Safety Compliance',
            'Precision Mounting',
            'Electrical Connections',
            'Quality Assurance'
        ]
    },
    {
        id: 4,
        title: 'REPAIR & MAINTENANCE',
        icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24" />
            </svg>
        ),
        description: 'Signage repair, replacement, and upgrading services to ensure long-lasting impact',
        features: [
            'Regular Maintenance',
            'LED Repairs',
            'Component Replacement',
            'Cleaning Services',
            'Upgrade Solutions'
        ]
    },
    {
        id: 5,
        title: 'EVENT & EXHIBITION BRANDING',
        icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 3v4M8 3v4M2 11h20" />
            </svg>
        ),
        description: 'Custom solutions for trade shows, exhibitions, and corporate events that make your brand stand out',
        features: [
            'Exhibition Booth Design',
            'Trade Show Graphics',
            'Event Signage',
            'Pop-up Displays',
            'Corporate Event Branding'
        ]
    },
    {
        id: 6,
        title: 'DIGITAL GRAPHICS',
        icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <path d="M8 21h8M12 17v4" />
            </svg>
        ),
        description: 'Customized digital signage solutions that combine creativity with functionality for modern businesses',
        features: [
            'Digital Display Systems',
            'Interactive Kiosks',
            'Menu Boards',
            'Content Management',
            'LED Video Walls'
        ]
    }
];

function ServicesPage() {
    const serviceRefs = useRef([]);
    const headerRef = useRef(null);

    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const serviceObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.opacity = '1';
                    entry.target.style.transform = 'translateY(0)';
                }
            });
        }, observerOptions);

        serviceRefs.current.forEach(ref => {
            if (ref) serviceObserver.observe(ref);
        });

        if (headerRef.current) {
            headerRef.current.style.opacity = '1';
            headerRef.current.style.transform = 'translateY(0)';
        }

        return () => serviceObserver.disconnect();
    }, []);

    return (
        <div className="services-page" style={{ paddingTop: '160px', paddingBottom: '100px', background: '#fff' }}>
            <SEO
                title="Our Services | Signage & Branding Services in Dubai, UAE"
                description="Comprehensive signage and branding services in Dubai, UAE. LED signage, neon signs, 3D lettering, vehicle wrapping, corporate branding, exhibition booths, wayfinding systems, digital graphics, installation, and maintenance. Rainbow Craft Signs & Graphics LLC."
                keywords="signage services dubai, LED signage services dubai, neon sign services dubai, 3D lettering services dubai, vehicle wrapping services dubai, corporate branding services dubai, exhibition booth services dubai, wayfinding services dubai, signage installation dubai, signage maintenance dubai, digital graphics dubai, branding solutions dubai"
                url="/services"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    "name": "Our Services - Rainbow Craft Signs & Graphics",
                    "description": "Comprehensive signage and branding services in Abu Dhabi, UAE.",
                    "url": "https://www.rainbowcraft.ae/services",
                    "itemListElement": servicesData.map((service, index) => ({
                        "@type": "ListItem",
                        "position": index + 1,
                        "item": {
                            "@type": "Service",
                            "name": service.title,
                            "description": service.description,
                            "provider": {
                                "@id": "https://www.rainbowcraft.ae"
                            },
                            "areaServed": {
                                "@type": "Country",
                                "name": "United Arab Emirates"
                            }
                        }
                    }))
                }}
            />
            <div className="container-wide">
                <div className="section-header-new" ref={headerRef} style={{
                    opacity: 0,
                    transform: 'translateY(30px)',
                    transition: 'opacity 0.8s ease, transform 0.8s ease',
                    marginBottom: '80px',
                    display: 'block'
                }}>
                    <h1 className="section-title-new" style={{ marginBottom: '20px' }}>OUR SERVICES</h1>
                    <div className="title-line" style={{ width: '60px', height: '4px', background: 'var(--primary-color)', marginBottom: '30px' }}></div>
                    <p className="hero-new-description" style={{
                        color: 'var(--text-color)',
                        maxWidth: '800px',
                        fontSize: '1.2rem',
                        opacity: 0.8
                    }}>
                        We specialize in LED and neon boards, 3D lettering, indoor & outdoor branding, wayfinding systems,
                        digital graphics, and customized signage solutions that combine creativity with functionality.
                    </p>
                </div>

                <div className="featured-grid">
                    {servicesData.map((service, index) => (
                        <div
                            key={service.id}
                            className="service-card-theme"
                            ref={el => serviceRefs.current[index] = el}
                            style={{
                                opacity: 0,
                                transform: 'translateY(50px)',
                                transition: `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`,
                                padding: '40px',
                                background: '#f9f9f9',
                                borderTop: '3px solid transparent',
                                cursor: 'default'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderTop = '3px solid var(--primary-color)';
                                e.currentTarget.style.transform = 'translateY(-10px)';
                                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderTop = '3px solid transparent';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <div className="service-icon" style={{
                                color: 'var(--primary-color)',
                                marginBottom: '25px',
                                transform: 'scale(1.2)',
                                transformOrigin: 'left',
                                display: 'inline-block'
                            }}>
                                {service.icon}
                            </div>
                            <h3 style={{
                                fontFamily: 'var(--font-heading)',
                                fontSize: '1.5rem',
                                color: 'var(--secondary-color)',
                                marginBottom: '15px',
                                letterSpacing: '-0.5px'
                            }}>{service.title}</h3>
                            <p style={{
                                lineHeight: '1.8',
                                color: 'var(--text-color)',
                                marginBottom: '30px',
                                fontWeight: 300,
                                fontSize: '0.95rem'
                            }}>{service.description}</p>

                            <ul className="service-features" style={{ listStyle: 'none', padding: 0 }}>
                                {service.features.map((feature, idx) => (
                                    <li key={idx} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        marginBottom: '10px',
                                        fontSize: '0.9rem',
                                        color: 'var(--text-color)',
                                        opacity: 0.9
                                    }}>
                                        <span style={{
                                            width: '6px',
                                            height: '6px',
                                            background: 'var(--primary-color)',
                                            borderRadius: '50%'
                                        }}></span>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default ServicesPage;
